/**
 * useFaceCapture — live webcam → face pose + blendshapes, in-browser, on a
 * plain camera. MediaPipe FaceLandmarker (WASM/GPU) runs each frame and writes
 * into faceBridge: head pose (yaw/pitch/roll) drives the character's Head bone
 * NOW; the ARKit-52 blendshapes are captured and exposed for a future face rig.
 *
 * This is the "motion as data" spike — the performer's own head drives the
 * avatar live (~a few landmarks of latency), and the same tiny pose payload is
 * what you'd broadcast to viewers instead of a video stream.
 *
 * Secure context required (getUserMedia) — the dev server is HTTPS. Desktop/GPU
 * first; phones run it slower.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { faceBridge } from '../state/faceBridge';

const WASM_URL = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm';
const MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

// Mirror + gain so it reads like driving the avatar with your own head. Tune
// live with ?facegain=1.2 ; signs chosen so turning your head right turns his.
const YAW_SIGN = -1, PITCH_SIGN = 1, ROLL_SIGN = -1;
const CLAMP = 0.7; // rad — keep the neck from snapping past a natural range

export default function useFaceCapture({ videoRef }) {
  const [status, setStatus] = useState('idle'); // idle | loading | live | error
  const [error, setError] = useState('');
  const landmarkerRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const lastTsRef = useRef(-1);
  const smooth = useRef({ yaw: 0, pitch: 0, roll: 0 });

  const stop = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    const v = videoRef.current;
    if (v) { try { v.pause(); v.srcObject = null; } catch { /* noop */ } }
    faceBridge.active = false;
    setStatus('idle');
  }, [videoRef]);

  const start = useCallback(async () => {
    setError(''); setStatus('loading');
    try {
      // Lazy-load the model once; reuse across toggles.
      if (!landmarkerRef.current) {
        const vision = await import('@mediapipe/tasks-vision');
        const fileset = await vision.FilesetResolver.forVisionTasks(WASM_URL);
        landmarkerRef.current = await vision.FaceLandmarker.createFromOptions(fileset, {
          baseOptions: { modelAssetPath: MODEL_URL, delegate: 'GPU' },
          outputFaceBlendshapes: true,
          outputFacialTransformationMatrixes: true,
          runningMode: 'VIDEO',
          numFaces: 1,
        });
      }
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480, facingMode: 'user' }, audio: false });
      streamRef.current = stream;
      const v = videoRef.current;
      v.srcObject = stream;
      await v.play();

      const gain = parseFloat(new URLSearchParams(window.location.search).get('facegain')) || 1.1;
      const m = new THREE.Matrix4();
      const q = new THREE.Quaternion();
      const e = new THREE.Euler();
      const pos = new THREE.Vector3();
      const scl = new THREE.Vector3();
      const clampv = (x) => Math.max(-CLAMP, Math.min(CLAMP, x));

      faceBridge.active = true;
      setStatus('live');

      const loop = () => {
        const vid = videoRef.current;
        if (!vid || vid.readyState < 2) { rafRef.current = requestAnimationFrame(loop); return; }
        let ts = performance.now();
        if (ts <= lastTsRef.current) ts = lastTsRef.current + 1; // strictly increasing
        lastTsRef.current = ts;
        const res = landmarkerRef.current.detectForVideo(vid, ts);

        const mat = res.facialTransformationMatrixes?.[0]?.data;
        if (mat) {
          m.fromArray(mat);
          m.decompose(pos, q, scl);
          e.setFromQuaternion(q, 'YXZ');
          const tYaw = clampv(YAW_SIGN * e.y * gain);
          const tPitch = clampv(PITCH_SIGN * e.x * gain);
          const tRoll = clampv(ROLL_SIGN * e.z * gain);
          // Light smoothing (One-Euro-lite): lerp toward target.
          const s = smooth.current;
          s.yaw += (tYaw - s.yaw) * 0.4;
          s.pitch += (tPitch - s.pitch) * 0.4;
          s.roll += (tRoll - s.roll) * 0.4;
          faceBridge.yaw = s.yaw; faceBridge.pitch = s.pitch; faceBridge.roll = s.roll;
        }
        const cats = res.faceBlendshapes?.[0]?.categories;
        if (cats) { const b = {}; for (const c of cats) b[c.categoryName] = c.score; faceBridge.blend = b; }

        rafRef.current = requestAnimationFrame(loop);
      };
      loop();
    } catch (err) {
      console.error('[FaceCapture] start failed:', err);
      setError(err?.message || 'Face capture failed to start');
      setStatus('error');
      stop();
    }
  }, [videoRef, stop]);

  useEffect(() => () => {
    stop();
    try { landmarkerRef.current?.close?.(); } catch { /* noop */ }
    landmarkerRef.current = null;
  }, [stop]);

  return { status, error, start, stop };
}
